import {create} from 'zustand'
import {TCompanyId, TRawFileId, TSubjectId} from "@/common/types";
import {
    IProtocol,
    IProtocolStep,
    IProtocolStepKind, IStimStep,
    TProtocolId,
    TProtocolStatus,
    TProtocolStepId,
} from "../types/protocol";
import * as protocolsService from "../services/protocols";
import {FileWithPath} from "@mantine/dropzone";
import {TProtocolDefinitionId} from "../types/protocolDefinitions";
import {deepMerge} from "../../utils/merging";
import {DeepPartial} from "../../utils/types/DeepPartial";
import {SupportedStimDevices, TStimDeviceKind} from "@/common/types/StimDevice.ts";
import {useWriteStatusStore, convertPayloadDatesToDayjs} from "@/protocols/persistence";
import {getWriteStore} from "@/protocols/persistence/writeStore";
import {uploadFileInBrowser, isUploadAbortError} from "@/protocols/persistence/uploadCoordinator";
import {useUploadStatusStore} from "@/protocols/persistence/useUploadStatusStore";

export type ProtocolsState = {
    protocols: Array<IProtocol>
    protocolsLoading: boolean
    protocolStepsLoading: boolean
    timezone: string
}
export type ProtocolsActions = {
    /**
     * Reset the store to initial state.
     */
    reset: () => void
    loadProtocol: (companyId: TCompanyId, pId: TProtocolId, steps: boolean) => Promise<IProtocol>
    loadProtocols: (companyId: TCompanyId, params: {
        subjectId?: TSubjectId,
        status?: TProtocolStatus,
        pdefId?: TProtocolDefinitionId
    }) => Promise<Array<IProtocol>>
    loadProtocolSteps: (companyId: TCompanyId, protocol: IProtocol) => Promise<IProtocol>
    addProtocol: (companyId: TCompanyId, protocol: IProtocol) => void
    updateProtocol: (companyId: TCompanyId, protocol: IProtocol) => void
    deleteProtocol: (companyId: TCompanyId, protocol: IProtocol) => void
    insertStep: (companyId: TCompanyId, protocol: TProtocolId, step: IProtocolStep, index: number) => void
    deleteStep: (companyId: TCompanyId, protocol: TProtocolId, step: IProtocolStep) => void
    /**
     * Update a step with partial updates. Uses deepMerge to combine
     * the partial updates with the existing step.
     * @param companyId - Company ID
     * @param protocolId - Protocol ID
     * @param stepId - Step ID to update
     * @param stepUpdates - Partial updates to apply to the step
     * @returns The merged step, or undefined if step was not found
     */
    updateStep: (companyId: TCompanyId, protocolId: TProtocolId, stepId: TProtocolStepId, stepUpdates: DeepPartial<IProtocolStep>) => IProtocolStep | undefined
    setTimezone: (timezone: string) => void
    addStepFiles: (companyId: TCompanyId, subjectId: TSubjectId, step: IProtocolStep, files: Array<FileWithPath>) => Promise<void>
    addProtocolFiles: (companyId: TCompanyId, subjectId: TSubjectId, protocolId: TProtocolId, files: Array<FileWithPath>) => Promise<void>
    deleteStepFile: (companyId: TCompanyId, protocolId: TProtocolId, stepId: TProtocolStepId, fileId: TRawFileId) => Promise<void>
    deleteProtocolFile: (companyId: TCompanyId, protocolId: TProtocolId, fileId: TRawFileId) => Promise<boolean>

}

export const useProtocolStore = create<ProtocolsState & ProtocolsActions>((set, get, store) => {
    const getProtocol = (state: ProtocolsState & ProtocolsActions, id: TProtocolId): IProtocol => {
        const protocol = state.protocols.find((p) => p.id === id)
        if (!protocol) {
            throw new Error(`Failed to find protocol id : ${id}`)
        }
        return protocol
    }
    const getProtocolStep = (state: ProtocolsState & ProtocolsActions, pid: TProtocolId, sid: TProtocolStepId): IProtocolStep => {
        const protocol = getProtocol(state, pid)
        const step = protocol.steps.find((step) => step.id === sid)
        if (!step) {
            throw new Error(`Failed to find step id:${sid} for protocl id: ${pid}`)
        }
        return step
    }
    const updateProtocol = (state: ProtocolsState & ProtocolsActions, protocol: IProtocol): Partial<ProtocolsState & ProtocolsActions> => {
        let index = state.protocols.findIndex((p) => p.id === protocol.id)
        if (index >= 0) {
            const newProtocols = [...state.protocols]
            newProtocols[index] = protocol
            return {protocols: newProtocols}
        } else {
            console.error(`Cannot find protocol with id:${protocol.id}`)
            return {}
        }
    }

    return ({
        protocols: [],
        protocolsLoading: false,
        protocolStepsLoading: false,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        reset: () => {
            set(store.getInitialState());
        },
        loadProtocol: async (companyId: TCompanyId, pId: TProtocolId, steps: boolean = true) => {
            set(() => ({protocolsLoading: true}))
            const protocol = await protocolsService.loadProtocol(companyId, pId, steps).finally(() => {
                set((_state) => ({protocolsLoading: false}))
            })

            // Fetch pending writes BEFORE computing the final state so the data
            // set happens once. Two separate sets (one with server data, one
            // with merged data) would let React render between them — the
            // uncontrolled <TextInput defaultValue={name}> would mount on the
            // first render with the OLD name and never update on re-renders,
            // so the user would see the OLD name despite the merged store
            // having the new name.
            //
            // Routes through getWriteStore() so browser mode (localStorage-backed)
            // merges queued writes just like Tauri mode does — otherwise a page
            // refresh mid-drain would leave the protocol view showing the stale
            // server response until the user exits and resumes.
            //
            // We fetch BOTH pending and dead rows so the merge reflects
            // every change the user made that hasn't reached the server
            // yet. Pending rows are in-flight (drainer is dispatching
            // them); dead rows failed to dispatch and need a retry.
            // For local state purposes they're equivalent: the user
            // made a change, the server doesn't have it yet, so we
            // apply the change on top of the server response. The
            // bootstrap auto-retries the dead rows so the drainer
            // picks them up shortly.
            const inflight = await getWriteStore().getPendingWrites({
                companyId,
                protocolId: pId,
                coalesceView: true,
                stateFilter: ['pending', 'dead'],
            })

            // Normalize server-returned ISO date strings to dayjs objects on every step.
            // Without this, ZoneAwareDateTimePicker (which calls value.tz(...)) crashes
            // for steps whose startTime/endTime came straight from the server rather
            // than from a pending write payload. convertPayloadDatesToDayjs is
            // idempotent — calling on already-dayjs values is a no-op.
            let finalProtocol: IProtocol = {
                ...protocol,
                steps: protocol.steps.map(s => convertPayloadDatesToDayjs(s) as IProtocolStep),
            }

            if (inflight.length > 0) {
                // Apply protocol_update writes first (replace top-level fields)
                const protocolUpdate = inflight.find(p => p.writeType === 'protocol_update' && p.coalesceKey === pId)
                if (protocolUpdate) {
                    finalProtocol = { ...finalProtocol, ...(protocolUpdate.payload as Partial<IProtocol>) }
                }

                // Then apply step_update writes per step (with date conversion).
                // The protocol_update payload above can include steps (it's the
                // full protocol at the time of write); after the JSON round-trip
                // through SQLite those step dates arrive as ISO strings. We
                // normalize each step's dates BEFORE the merge decision so that
                // even steps without their own step_update pending write end up with
                // dayjs startTime/endTime — otherwise the picker crashes on
                // 'value.tz is not a function'.
                const updatedSteps = finalProtocol.steps.map(step => {
                    const stepWithDates = convertPayloadDatesToDayjs(step) as IProtocolStep
                    const pendingForStep = inflight.find(p => p.coalesceKey === step.id && p.writeType === 'step_update')
                    if (!pendingForStep) return stepWithDates
                    const payloadWithDates = convertPayloadDatesToDayjs(pendingForStep.payload) as DeepPartial<IProtocolStep>
                    // deepMerge uses immer's produce under the hood, which is
                    // non-mutating and preserves references to unmodified subtrees
                    // (including dayjs instances). It also short-circuits on
                    // $isDayjsObject to avoid recursing into dayjs.
                    return deepMerge(stepWithDates, payloadWithDates) as IProtocolStep
                })
                finalProtocol = { ...finalProtocol, steps: updatedSteps }
            }

            // Single set with the fully-merged state.
            set((state) => {
                const index = state.protocols.findIndex((p) => p.id === finalProtocol.id)
                if (index >= 0) {
                    return updateProtocol(state, finalProtocol)
                } else {
                    return {protocols: [...state.protocols, finalProtocol]}
                }
            })

            if (inflight.length > 0) {
                useWriteStatusStore.getState().refresh(companyId, pId)
            }
            return protocol
        },
        loadProtocols: async (companyId, params) => {
            try {
                set((_state) => ({protocolsLoading: true}))
                const protocols = await protocolsService.loadProtocols(companyId, params).finally(() => {
                    set((_state) => ({protocolsLoading: false}))
                })
                set((_state) => ({protocols}))
                return protocols
            } catch (error) {
                console.error(error)
                return []
            }
        },
        loadProtocolSteps: async (companyId: TCompanyId, protocol: IProtocol) => {
            try {
                set((_state) => ({protocolStepsLoading: true}))
                const steps = await protocolsService.loadProtocolSteps(companyId, protocol.id).finally(() => {
                    set((_state) => ({protocolStepsLoading: false}))
                })
                set((state) => updateProtocol(state, {...protocol, steps}))
                return protocol
            } catch (error) {
                console.error(error)
                return protocol
            }
        },
        addProtocol: (_companyId: TCompanyId, protocol) => {
            set((state) => ({protocols: [protocol, ...state.protocols]}))
        },
        updateProtocol: async (_companyId: TCompanyId, protocol: IProtocol) => {
            set((state) => updateProtocol(state, protocol))
        },
        deleteProtocol: (_companyId: TCompanyId, protocol: IProtocol) => {
            set((state) => {
                const index = state.protocols?.findIndex((p) => p.id === protocol.id)
                if (index < 0) {
                    console.error(`Failed to find protocol id:${protocol.id}`)
                    return {}
                }
                const newProtocols = [...state.protocols]
                newProtocols.splice(index, 1)
                return {...state, protocols: newProtocols}

            })
        },
        insertStep: (_companyId: TCompanyId, protocolId: TProtocolId, step: IProtocolStep, index: number) => {
            let updatedProtocol = undefined
            set((state) => {
                const protocol = getProtocol(state, protocolId)
                const newSteps = [...protocol.steps.slice(0, index), step, ...protocol.steps.slice(index)]
                updatedProtocol = {...protocol, steps: newSteps}
                return updateProtocol(state, updatedProtocol)
            })
            return updatedProtocol
        },
        deleteStep: (_companyId: TCompanyId, protocolId: TProtocolId, step: IProtocolStep) => {
            let updatedProtocol = undefined
            set((state) => {
                    const protocol = getProtocol(state, protocolId)
                    const newSteps = [...protocol.steps]
                    const index = protocol.steps.findIndex((st) => st.id === step.id)
                    newSteps.splice(index, 1)
                    updatedProtocol = {...protocol, steps: newSteps}
                    if (index >= 0) {
                        return updateProtocol(state, updatedProtocol)
                    } else {
                        console.error(`Cannot find ${step.id} in ${protocol.id}`)
                        return {}
                    }
                }
            )
            return updatedProtocol
        },
        updateStep: (_companyId: TCompanyId, protocolId: TProtocolId, stepId: TProtocolStepId, stepUpdates: DeepPartial<IProtocolStep>): IProtocolStep | undefined => {
            let mergedStep: IProtocolStep | undefined;
            set((state) => {
                const protocol = getProtocol(state, protocolId)
                const index = protocol.steps.findIndex((st) => st.id === stepId)
                if (index < 0) {
                    throw Error(`Cannot find step.id:${stepId} in protocol.id:${protocol.id}`)
                }

                const existingStep = protocol.steps[index]
                if (existingStep.kind === IProtocolStepKind.STIM_EVENT) {
                    const newStepUpdates = stepUpdates as DeepPartial<IStimStep>
                    const existingDeviceKind = existingStep.stimMetadata?.stimDevice?.kind

                    const newDeviceKind = newStepUpdates.stimMetadata?.stimDevice?.kind

                    if (newDeviceKind && newDeviceKind !== existingDeviceKind) {
                        const newStimMeta = newStepUpdates.stimMetadata || {}
                        if (newDeviceKind === TStimDeviceKind.BscPrograms) {
                            newStimMeta.config =undefined
                            newStimMeta.program_name =undefined
                            newStimMeta.stimDevice = SupportedStimDevices[1]
                        } else if (newDeviceKind === TStimDeviceKind.NeuroTracSports) {
                            newStimMeta.program_name = undefined
                            newStimMeta.config = undefined
                            newStimMeta.stimDevice = SupportedStimDevices[0]
                        }
                        newStepUpdates.stimMetadata = newStimMeta
                    }
                }

                // Use deepMerge to combine existing step with partial updates
                mergedStep = deepMerge(existingStep, stepUpdates) as IProtocolStep

                const newSteps = [...protocol.steps]
                newSteps[index] = mergedStep
                const updatedProtocol = {...protocol, steps: newSteps}
                return updateProtocol(state, updatedProtocol)
            })
            return mergedStep
        },
        setTimezone: (timezone: string) => {
            set((_state) => ({timezone}))
        },
        /**
         *  This method is designed to do the following for 1 or more files.
         *  - Request a File Upload (creates a oooooooo serverside representing the pending upload)
         *  - Upload the file Updating upload progress as the file is uploaded.
         *  - Associate the file to the step
         *
         *  Uses the unified progress tracking system for consistent state management.
         *
         * @param companyId
         * @param subjectId
         * @param step
         * @param files
         */
        addStepFiles: async (companyId: TCompanyId, subjectId: TSubjectId, step: IProtocolStep, files: Array<FileWithPath>) => {
            // Browser-mode file upload flow. Each file is registered in
            // useUploadStatusStore.browserUploads so the file list can
            // show the in-flight XHR progress. The XHR's own onUploadProgress
            // (forwarded by BrowserUploadStore.finalizeBrowserUpload via
            // the onProgress callback in uploadFileInBrowser) drives the
            // slice entry's progress.
            //
            // Tauri-mode uploads do NOT go through this path — they use
            // TauriDragDropZone -> store.enqueuePickedFiles (drag/drop) or
            // store.pickFile -> store.enqueuePickedFile (native picker).
            for (const file of files) {
                const abort = new AbortController()
                // The File + upload args are retained on the entry so a
                // failed upload can be retried (auto-retry on reconnect or
                // manual Retry) without re-picking the file.
                const tempId = useUploadStatusStore.getState().startBrowserUpload(
                    file.name, file.size, abort, file,
                    {
                        companyId: companyId as unknown as string,
                        protocolId: step.protocolId as unknown as string,
                        subjectId: subjectId as unknown as string,
                        protocolStepId: step.id as unknown as string,
                    },
                )
                try {
                    await uploadFileInBrowser({
                        companyId: companyId as unknown as string,
                        protocolId: step.protocolId as unknown as string,
                        file,
                        subjectId: subjectId as unknown as string,
                        protocolStepId: step.id as unknown as string,
                        signal: abort.signal,
                        onProgress: (bytesWritten, totalBytes) => {
                            useUploadStatusStore.getState().updateBrowserUploadProgress(
                                tempId, bytesWritten, bytesWritten / totalBytes,
                            )
                        },
                    })
                    useUploadStatusStore.getState().completeBrowserUpload(tempId)
                } catch (e) {
                    if (isUploadAbortError(e)) {
                        useUploadStatusStore.getState().cancelBrowserUpload(tempId)
                    } else {
                        const err = e instanceof Error ? e.message : String(e)
                        useUploadStatusStore.getState().failBrowserUpload(tempId, err)
                        console.error('[addStepFiles] upload failed', e)
                        const { showErrorNotification } = await import('@/common/notifications')
                        showErrorNotification({
                            title: 'Upload failed',
                            message: `Could not upload ${file.name}: ${err}`,
                        })
                    }
                }
            }
        },
        deleteStepFile: async (companyId: TCompanyId, pid: TProtocolId, sid: TProtocolStepId, fid: TRawFileId) => {
            const success = await protocolsService.deleteProtocolStepFile(companyId, pid, sid, fid).catch((_err) => {
                return false
            })
            if (success) {
                const step = getProtocolStep(get(), pid, sid)
                const updatedFiles = step.transient?.fileData?.files?.filter((file) => file.id !== fid)
                get().updateStep(companyId, pid, sid, {
                    transient: {
                        ...step.transient,
                        fileData: {
                            ...step.transient?.fileData,
                            files: updatedFiles
                        }
                    }
                })
            } else {
                console.error(`deleteProtocolStepFile(${companyId}, ${pid}, ${sid}, ${fid}) returned false`)
            }
        },
        addProtocolFiles: async (companyId: TCompanyId, subjectId: TSubjectId, protocolId: TProtocolId, files: Array<FileWithPath>) => {
            // Browser-mode file upload flow (see addStepFiles for details).
            // Tauri-mode uploads do NOT go through this path; they use
            // TauriDragDropZone -> store.enqueuePickedFiles (drag/drop) or
            // store.pickFile -> store.enqueuePickedFile (native picker).
            for (const file of files) {
                const abort = new AbortController()
                // Retain File + args for retry (see addStepFiles).
                const tempId = useUploadStatusStore.getState().startBrowserUpload(
                    file.name, file.size, abort, file,
                    {
                        companyId: companyId as unknown as string,
                        protocolId: protocolId as unknown as string,
                        subjectId: subjectId as unknown as string,
                    },
                )
                try {
                    await uploadFileInBrowser({
                        companyId: companyId as unknown as string,
                        protocolId: protocolId as unknown as string,
                        file,
                        subjectId: subjectId as unknown as string,
                        signal: abort.signal,
                        onProgress: (bytesWritten, totalBytes) => {
                            useUploadStatusStore.getState().updateBrowserUploadProgress(
                                tempId, bytesWritten, bytesWritten / totalBytes,
                            )
                        },
                    })
                    useUploadStatusStore.getState().completeBrowserUpload(tempId)
                } catch (e) {
                    if (isUploadAbortError(e)) {
                        useUploadStatusStore.getState().cancelBrowserUpload(tempId)
                    } else {
                        const err = e instanceof Error ? e.message : String(e)
                        useUploadStatusStore.getState().failBrowserUpload(tempId, err)
                        console.error('[addProtocolFiles] upload failed', e)
                        const { showErrorNotification } = await import('@/common/notifications')
                        showErrorNotification({
                            title: 'Upload failed',
                            message: `Could not upload ${file.name}: ${err}`,
                        })
                    }
                }
            }
        },
        
        deleteProtocolFile: async (companyId: TCompanyId, protocolId: TProtocolId, fileId: TRawFileId) => {
            const success = await protocolsService.deleteProtocolFile(companyId, protocolId, fileId).catch((_err) => {
                return false
            })
            if (!success) {
                console.error(`deleteProtocolFile(${companyId}, ${protocolId}, ${fileId}) returned false`)
            }
            return success
        },
    })
})
